// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';
import { FeatureCollection } from 'geojson';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import {
  SidePanelFactory,
  CollapseButtonFactory,
  PanelHeaderFactory,
  PanelTabFactory,
  injectComponents
} from 'kepler.gl/components.js';
import { SIDEBAR_PANELS } from 'kepler.gl/constants.js';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';

import { addDataToMap, updateMap, removeDataset } from 'kepler.gl/actions.js';
import { processGeojson } from 'kepler.gl/processors.js';

import { ContextType, FilterContext } from '../../../../../types';
import { config } from '../../../../../config/config';
import { SensorDO } from '../../../../sensors/requests';

const keplerGlGetState = (state: any): any => state.keplerGl;

const MAP_STYLES: any[] = [
  {
    id: 'wdna',
    label: 'WDNA',
    url: config.mapRootUrl,
    icon: '',
  },
];

// CUSTOMIZE SIDE-PANEL
// To remove collapse button in side panel
const CustomCollapseButtonFactory = (...args: any[]): any => {
  return ({ onClick, isOpen }: any): any => (null);
};

const CustomPanelHeaderFactory = (...args: any[]): any => {
  // const CustomPanelHeader = () => null;
  const CustomPanelHeader: any = PanelHeaderFactory(...args);

  /*
  // Custom logo on header (disables share action bug??)
  CustomPanelHeader.defaultProps = {
    ...CustomPanelHeader.defaultProps,
    logoComponent: () => { return <img src="./assets/img/logos/wdna_white_sm.png" />; },
  };
  */

  return CustomPanelHeader;
};

const CustomPanelTabFactory = (...args: any[]): any => {
  return (): any => null;
};

const CustomSidePanelFactory = (...args: any[]): any => {
  const CustomSidePanel: any = SidePanelFactory(...args);

  CustomSidePanel.defaultProps = {
    ...CustomSidePanel.defaultProps,
    panels: SIDEBAR_PANELS.filter(({
      id
    }: any) => id === 'filter')
  };

  return CustomSidePanel;
};

CustomSidePanelFactory.deps = SidePanelFactory.deps;

// Inject custom components
const KeplerGl: any = injectComponents([
  [SidePanelFactory, CustomSidePanelFactory],
  [CollapseButtonFactory, CustomCollapseButtonFactory],
  // [PanelHeaderFactory, CustomPanelHeaderFactory],
  [PanelTabFactory, CustomPanelTabFactory]
]);

type Props = unknown;

interface State {
  geojson?: FeatureCollection;
}

export default class Container extends React.Component<Props, State> {
  private mounted: boolean;

  public static contextType: React.Context<ContextType> = FilterContext;
  public context: React.ContextType<typeof FilterContext> | undefined;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;

    this.state = {
      geojson: undefined
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
  }

  public componentDidUpdate(): void {
    const { mapData: newGeojson, sensorInfo } = this.context?.parentState || {};
    if (newGeojson && sensorInfo && this.isNewData(newGeojson)) {
      this.updateData(sensorInfo, newGeojson);
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public isNewData = (newGeojson: FeatureCollection): boolean => {
    const { geojson: currGeojson } = this.state;
    return !_.isEqual(newGeojson, currGeojson);
  };

  public updateData = (sensorInfo: SensorDO, geojson: FeatureCollection): void => {
    if (this.mounted) {
      this.setLocalState({
        geojson
      });

      const data: any = {
        datasets: {
          info: {
            label: sensorInfo.name,
            id: '0000'
          },
          data: processGeojson(geojson)
        },
        option: {
          centerMap: true,
          keepExistingConfig: true
        }
      };

      window.store.dispatch(removeDataset('0000'));
      window.store.dispatch(addDataToMap(data));
      window.store.dispatch(updateMap({ zoom: 9.5 }));
    }
  };

  public render(): JSX.Element {
    return (
      <div id="content" style={{ width: '99%' }}>
        <WidgetGrid>
          <JarvisWidget
            editbutton={false}
            togglebutton={false}
            colorbutton={false}
            deletebutton={false}
            custombutton={false}
            fullscreenbutton={false}
            height="calc(100vh - 160px)"
          >
            <div className="widget-body">
              <AutoSizer>
                {({ height, width }: any): any => (
                  <KeplerGl
                    mapboxApiAccessToken="TEST"
                    id="map"
                    getState={keplerGlGetState}
                    mapStyles={MAP_STYLES}
                    width={width}
                    height={height}
                  />
                )}
              </AutoSizer>
            </div>
          </JarvisWidget>
        </WidgetGrid>
      </div>
    );
  }
}
